import { useEffect } from 'react';
import Layout from '../../../../components/layout/Layout';
import Breadcrumb from '../../../../components/layout/Breadcrumb';
import { updateSEO } from '../../../../utils/seo';

// ===== 異文化理解研修 詳細 全面作り直し（Implementer C スコープ） =====
// デザイン土台: home / about/overview の新スタイル。実データ変更禁止・現行working tree実文のみ・創作禁止。
// 【厳守】本文は「外部検証可能記述→内部完結」への差替済み表現をそのまま保持（巻き戻し禁止）。

function NavyBand({ no, ja, en }: { no: string; ja: string; en: string }) {
  return (
    <div className="bg-[#1a2942] border-y border-[#a38e65]/25">
      <div className="max-w-[1680px] mx-auto px-4 sm:px-6 lg:px-10 py-5 sm:py-[26px] flex items-center gap-4 sm:gap-6">
        <img src="/images/brand/jba-logo.png" alt="" aria-hidden="true" className="h-8 w-8 sm:h-9 sm:w-9 object-contain shrink-0 opacity-90" />
        <span className="font-['Playfair_Display',serif] text-[#d7b779] text-xl sm:text-[26px] tracking-[0.1em] shrink-0 leading-none">{no}</span>
        <h2 className="jba-display font-medium text-[#f8f6f0] text-lg sm:text-2xl lg:text-[26px] tracking-[0.08em] leading-tight">{ja}</h2>
        <span className="hidden md:block ml-auto font-['Playfair_Display',serif] italic text-[#a38e65] text-base lg:text-[19px] tracking-[0.12em]">{en}</span>
      </div>
    </div>
  );
}

const BTN08_ON_LIGHT = "relative inline-flex items-center justify-center bg-[#1a2942] text-[#f8f6f0] border border-[#a38e65] px-[42px] py-[16px] text-sm tracking-[0.2em] font-['Noto_Serif_JP',serif] transition-colors duration-300 hover:bg-[#a38e65] hover:text-[#1a2942] hover:border-[#1a2942] cursor-pointer whitespace-nowrap before:content-[''] before:absolute before:inset-1 before:border before:border-[#a38e65]/45 before:pointer-events-none before:transition-colors before:duration-300 hover:before:border-[#1a2942]/45";
const BTN08_ON_NAVY = "relative inline-flex items-center justify-center bg-[#a38e65] text-white font-semibold border border-[#1a2942] px-[42px] py-[16px] text-sm tracking-[0.2em] font-['Noto_Serif_JP',serif] transition-colors duration-300 hover:bg-[#1a2942] hover:text-[#f8f6f0] hover:border-[#a38e65] cursor-pointer whitespace-nowrap before:content-[''] before:absolute before:inset-1 before:border before:border-[#1a2942]/40 before:pointer-events-none before:transition-colors before:duration-300 hover:before:border-[#a38e65]/50";
const TEXT_ARROW = "inline-flex items-center gap-2 font-['Noto_Serif_JP',serif] text-[15px] text-[#1a2942] tracking-[0.06em] border-b border-[#a38e65] pb-1 hover:text-[#8f7a56] transition-colors duration-300";

const OVERVIEW = [
  '高級ホテルや富裕層向けサービスの現場では、世界各国からのゲストへの対応が日常的に求められます。ヨーロッパの貴族、中東の王族、アジアの富裕層、北米のビジネスエリートなど、文化的背景が異なるゲスト一人ひとりに最適なサービスを提供するには、深い文化理解が必要です。',
  '本研修では、執事が実践する異文化対応の技術を理論と実践の両面から学びます。国際プロトコル（国際儀礼）の基本原則、主要国・地域の文化的特徴と習慣、宗教（キリスト教、イスラム教、仏教、ヒンドゥー教など）に基づく配慮事項、食事制限とハラール対応、非言語コミュニケーションの文化差、多言語環境でのコミュニケーション技術まで、実務で直面する具体的な場面を想定した内容を習得。ロールプレイとケーススタディを通じて、実践的な異文化対応力を体得します。',
  '高級ホテル、レストラン、コンシェルジュ、旅行業、金融機関など、海外からのVIPに接する機会のある業種の方に最適な研修です。',
];

const FEATURES = [
  { icon: 'ri-global-line', t: '実践的知識', d: '現場で使える具体的配慮事項を学びます。' },
  { icon: 'ri-building-line', t: '宗教理解', d: '食事・習慣の配慮方法を習得します。' },
  { icon: 'ri-user-star-line', t: '即応力養成', d: 'ロールプレイで実践します。' },
];

const CURRICULUM = [
  { part: '第1部', time: '45分', title: '国際プロトコルの基礎', desc: '国際儀礼の基本原則、席次と序列の考え方、国旗・国歌の扱い方、贈答のマナーとタブーを学びます。執事が実践する国際的な場での礼儀作法、外交プロトコルの基本、VIPゲストに対する適切な敬意の示し方を理解し、現代のサービス業での応用方法を習得します。理論だけでなく、実際の執事の現場での事例を通じて、効果的な国際プロトコルのあり方を深く学びます。' },
  { part: '第2部', time: '45分', title: '主要国・地域の文化理解', desc: '欧米文化圏の特徴と配慮点、中東・イスラム文化圏の習慣、アジア文化圏の多様性、それぞれの価値観と行動様式を習得します。執事が実践する「相手の文化を尊重する」姿勢、各国の歴史的背景に基づく価値観の理解、文化的な違いを活かしたサービス提供の方法を学びます。グループワークを通じて実践的なスキルを身につけます。' },
  { part: '第3部', time: '45分', title: '宗教的配慮と実務対応', desc: '主要宗教の基礎知識、食事制限（ハラール、コーシャ、ベジタリアン）への対応、祈りの時間と場所の配慮、服装規定の理解を学びます。執事が実践する「宗教的多様性への敬意」、各宗教の重要な祭日や慣習への配慮、実際のサービス現場での具体的な対応方法など、宗教的背景を理解したサービス提供技術を習得します。' },
  { part: '第4部', time: '45分', title: '非言語コミュニケーションとケーススタディ', desc: 'ジェスチャーの文化差、パーソナルスペースの違い、アイコンタクトの意味、実際のケースを用いた演習で実践力を養います。執事が実践する「言葉を超えたコミュニケーション」技術、文化的背景による非言語表現の違い、誤解を避けるための配慮方法を学びます。実際のケーススタディを通じて、現場で使える異文化対応スキルを身につけます。' },
];

const RECOMMENDS = [
  '高級ホテル・旅館（インバウンド対応）',
  'ファインダイニング・高級レストラン',
  'コンシェルジュサービス',
  'VIP向け旅行サービス',
  'プライベートバンク・金融機関',
  '高級不動産・会員制サービス',
  '免税店・ラグジュアリーブランド',
  '国際会議場・イベント施設',
];

const OUTCOMES = [
  { t: '国際的な配慮力', d: '各国の文化・宗教を理解し、適切に配慮したサービスを提供できる力を身につけます。執事の国際的な現場経験に基づく実践的な配慮技術を習得します。' },
  { t: 'VIP対応力の向上', d: '海外富裕層に対する高品質なおもてなしを実践できるスキルを習得します。国際プロトコルに基づく適切な対応技術を身につけます。' },
  { t: 'トラブル回避能力', d: '文化的誤解やタブーを事前に察知し、問題を未然に防ぐ力を養います。冷静な判断力と文化的配慮技術を習得します。' },
  { t: 'グローバルマインドの醸成', d: '多様性を尊重し、柔軟に対応できる国際感覚を身につけます。異文化への理解と敬意を持った対応力を養います。' },
];

const SPECS = [
  { k: '研修時間', v: '1日3時間（午前/午後選択可）' },
  { k: '対象人数', v: '10-20名' },
  { k: '対象レベル', v: '中級〜上級（VIP対応スタッフ）' },
  { k: '実施場所', v: '貴社会議室または当協会会場' },
  { k: 'カスタマイズ', v: '内容調整可能（別途相談）' },
];

const INCLUDES = ['研修プログラム実施', 'テキスト教材（参加者人数分）', '文化理解ハンドブック', '修了証（参加者全員）', '講師交通費（都内の場合）', '事後フォローアップ（1回）'];

const FLOW = [
  { t: 'お問い合わせ・ご相談', d: 'まずはお気軽にお問い合わせください。貴社の課題や目標をヒアリングいたします。' },
  { t: 'ヒアリング・カスタマイズ', d: '対応する主な国籍、現場での課題、研修の目的などを詳しく伺い、最適なプログラムを設計します。' },
  { t: '日程調整・ご契約', d: '実施日時、場所、料金を確定し、ご契約となります。' },
  { t: '研修実施', d: '国際経験豊富な講師による実践的な研修を実施します。' },
  { t: 'フォローアップ', d: '研修後も継続的にフォローアップし、現場での実践をサポートします。' },
];

const FAQS = [
  { q: 'オンラインでの実施は可能ですか？', a: 'はい、オンライン形式での実施も対応可能です。別途お見積もりいたします。' },
  { q: '英語力は必要ですか？', a: '本研修は異文化理解が目的で、英会話力は不問です。' },
  { q: '特定の国に特化した内容にカスタマイズできますか？', a: 'はい、貴社が主に対応する国・地域に特化した内容への変更が可能です。' },
  { q: 'ハラール対応について詳しく学べますか？', a: 'はい、第3部でイスラム教の食事規定とハラール対応を詳しく学びます。' },
  { q: '実施場所はどこになりますか？', a: '貴社会議室での実施が基本です。当協会会場での実施も可能です。' },
  { q: '事前準備は必要ですか？', a: '現在対応している国籍や困った事例をメモしてご参加ください。' },
  { q: 'フォローアップの内容は？', a: '研修後1ヶ月以内に、実践状況を確認し、追加アドバイスを行います。' },
  { q: '修了証は発行されますか？', a: 'はい、参加者全員に修了証を発行いたします。' },
  { q: '他の研修と組み合わせることは可能ですか？', a: 'はい。複数研修の組み合わせも可能です。割引もございます。' },
  { q: '講師はどのような方ですか？', a: '海外での執事経験を持つMaster Butler資格保持者が担当します。' },
  { q: '文化理解ハンドブックの内容は？', a: '主要国の文化・習慣・タブーをまとめた実務で使える参考資料です。' },
  { q: '中国からのゲストへの対応も学べますか？', a: 'はい、アジア文化圏として中国の文化的特徴も詳しく学びます。' },
];

const RELATED = [
  { label: 'ホスピタリティ向上研修', href: '/seminars/corporate/hospitality-enhancement', desc: '執事に学ぶ心からのおもてなしの技術' },
  { label: 'コミュニケーション技術研修', href: '/seminars/corporate/communication-skills', desc: '執事に学ぶ傾聴力・表現力・非言語コミュニケーションの技術' },
  { label: '企業向け研修プログラム一覧', href: '/seminars', desc: 'すべての研修プログラムを見る' },
];

export default function CrossCulturalUnderstandingPage() {
  useEffect(() => {
    document.title = '異文化理解研修｜グローバルコミュニケーション｜全日本執事協会';
    updateSEO({
      title: '異文化理解研修｜グローバルコミュニケーション｜全日本執事協会',
      description: '全日本執事協会の異文化理解研修。グローバルなビジネス環境での文化的違いを理解し、効果的なコミュニケーションスキルを身につける企業研修プログラムです。',
      canonical: 'https://butler-association.jp/seminars/corporate/cross-cultural-understanding',
    });
  }, []);

  const breadcrumbs = [
    { label: 'ホーム', href: '/' },
    { label: 'セミナー・イベント', href: '/seminars' },
    { label: '異文化理解研修' },
  ];

  return (
    <Layout>
      <section className="relative bg-[#1a2942] overflow-hidden min-h-[500px] lg:min-h-[580px] flex items-center">
        <img src="/images/generated/jba-corp-cross-cultural.webp" alt="異文化理解研修で各国のプロトコルを指導する国際経験豊富な執事と受講者" className="absolute inset-0 w-full h-full object-cover object-center" />
        <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(16,20,26,0.92)_0%,rgba(16,20,26,0.72)_48%,rgba(16,20,26,0.35)_100%)]"></div>
        <div className="relative z-10 w-full max-w-[1680px] mx-auto px-4 sm:px-6 lg:px-10 pt-32 pb-16 lg:pt-36 lg:pb-20">
          <div className="max-w-[720px]">
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.3em] text-xs sm:text-[13px] text-[#d7b779] mb-4">Cross-Cultural Understanding</span>
            <div className="w-12 h-px bg-[#a38e65] mb-6"></div>
            <h1 className="jba-display font-normal text-white text-[clamp(26px,3.2vw,42px)] leading-[1.4] tracking-[0.04em] mb-6">異文化理解研修</h1>
            <p className="font-['Noto_Serif_JP',serif] text-base sm:text-lg text-white/90 leading-[1.95]">
              執事が国際的な環境で培ってきた異文化対応力を、富裕層向けサービス業に応用する実践的プログラムです。執事は世界中から訪れるゲストに対応するため、各国の文化、宗教、習慣、プロトコルを深く理解している必要があります。単なる知識ではなく、相手の文化的背景を尊重し、適切に配慮する実践的スキルは、グローバル化する現代のサービス業に不可欠です。本研修では、国際プロトコルの基礎、主要国の文化的特徴、宗教的配慮、非言語コミュニケーション、多言語対応まで、VIP対応に必要な国際感覚を体系的に習得します。
            </p>
          </div>
        </div>
      </section>

      <Breadcrumb items={breadcrumbs} />

      <div className="bg-white">
        <div className="max-w-[1680px] mx-auto px-4 sm:px-6 lg:px-10 pt-10">
          <p className="border-l-2 border-[#a38e65] bg-[#f8f6f0] px-5 py-4 font-['Noto_Serif_JP',serif] text-sm text-[#4a4638] leading-[1.9]">
            ⚠️ 本研修はスキル習得を目的としており、外国語能力の向上や海外赴任を保証するものではありません。
          </p>
        </div>
      </div>

      <NavyBand no="I" ja="この研修について" en="Overview" />
      <section className="py-12 sm:py-16 bg-white">
        <div className="max-w-[1680px] mx-auto px-4 sm:px-6 lg:px-10">
          <div className="grid lg:grid-cols-[1fr_360px] gap-10 lg:gap-14 items-start mb-12 sm:mb-14">
            <div>
              <h3 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[30px] leading-[1.5] tracking-[0.02em] mb-6">
                世界のゲストに、<span className="text-[#a38e65]">敬意をもって寄り添う</span>
              </h3>
              <div className="space-y-[18px] font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95]">
                {OVERVIEW.map((p, i) => <p key={i}>{p}</p>)}
              </div>
            </div>
            <img src="/images/generated/jba-p-035-cross-greet.webp" alt="多様な文化背景を持つゲストへの対応を学ぶ国際交流の場面" className="w-full h-64 lg:h-[380px] object-cover border border-[#a38e65]/30" />
          </div>
          <div className="grid sm:grid-cols-3 border-t-2 border-[#1a2942]">
            {FEATURES.map((f) => (
              <div key={f.t} className="flex flex-col gap-4 border-b border-[#e2ddd2] sm:border-b-0 sm:border-r last:border-r-0 py-7 sm:px-6 first:sm:pl-0">
                <span className="inline-flex items-center justify-center w-12 h-12 border border-[#a38e65]/50 text-[#a38e65]"><i className={`${f.icon} text-2xl`}></i></span>
                <h4 className="jba-display font-medium text-[#1a2942] text-lg sm:text-xl">{f.t}</h4>
                <p className="font-['Noto_Serif_JP',serif] text-sm text-[#4a4638] leading-[1.9]">{f.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 新バリエーション①（番号付き時間バッジ・カリキュラム。由来: home IV 全幅エディトリアル行型の翻案） */}
      <NavyBand no="II" ja="研修カリキュラム（1日3時間）" en="Curriculum" />
      <section className="py-12 sm:py-16 bg-white">
        <div className="max-w-[1680px] mx-auto px-4 sm:px-6 lg:px-10">
          <div className="max-w-4xl mb-10 text-left">
            <p className="font-['Noto_Serif_JP',serif] text-base sm:text-lg text-[#333] leading-[1.95]">
              異文化理解研修では、執事の国際的な現場経験に基づく4つの分野を体系的に学びます。各部は実践的な内容で構成され、知識だけでなく実際の異文化対応技術も習得できます。
            </p>
          </div>
          <img src="/images/generated/seq-cross-cultural-training-ba8e14.webp" alt="異なる文化背景の参加者が会食形式でマナーを学ぶ異文化理解研修" className="w-full aspect-[21/9] h-auto object-cover object-center border border-[#a38e65]/30 mb-10" />
          <div className="border-t-2 border-[#1a2942]">
            {CURRICULUM.map((c) => (
              <div key={c.part} className="flex flex-col md:flex-row gap-4 md:gap-10 border-b border-[#e2ddd2] py-7">
                <div className="md:w-[220px] md:shrink-0">
                  <div className="flex items-center gap-3">
                    <span className="font-['Playfair_Display',serif] text-[#d7b779] text-2xl leading-none">{c.part}</span>
                    <span className="inline-block border border-[#a38e65]/60 text-[#8f7a56] text-xs tracking-[0.06em] px-2.5 py-1 font-['Noto_Serif_JP',serif]">{c.time}</span>
                  </div>
                  <h4 className="jba-display font-medium text-[#1a2942] text-lg sm:text-xl leading-[1.5] mt-3">{c.title}</h4>
                </div>
                <p className="flex-1 font-['Noto_Serif_JP',serif] text-[15px] text-[#4a4638] leading-[1.95]">{c.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <NavyBand no="III" ja="こんな企業・部門におすすめ" en="Ideal For" />
      <section className="py-12 sm:py-16 bg-white">
        <div className="max-w-[1680px] mx-auto px-4 sm:px-6 lg:px-10">
          <div className="grid sm:grid-cols-2 border-t border-[#e2ddd2]">
            {RECOMMENDS.map((r) => (
              <div key={r} className="flex items-center gap-4 border-b border-[#e2ddd2] py-5 sm:px-5 first:sm:pl-0">
                <i className="ri-check-line text-xl text-[#a38e65] shrink-0"></i>
                <span className="font-['Noto_Serif_JP',serif] text-[15px] text-[#1a2942]">{r}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <NavyBand no="IV" ja="研修で得られる成果" en="Outcomes" />
      <section className="py-12 sm:py-16 bg-white">
        <div className="max-w-[1680px] mx-auto px-4 sm:px-6 lg:px-10">
          <img src="/images/generated/jba-p-036-cross-table.webp" alt="研修を経て国際的なおもてなしを実践する受講者の様子" className="w-full aspect-[21/9] h-auto object-cover object-top border border-[#a38e65]/30 mb-10" />
          <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
            {OUTCOMES.map((o, i) => (
              <div key={o.t} className="bg-[#1a2942] border border-[#a38e65]/45 p-7 sm:p-8 flex flex-col">
                <div className="flex items-baseline gap-4 mb-4">
                  <span className="font-['Playfair_Display',serif] text-[#d7b779] text-3xl leading-none">{String(i + 1).padStart(2, '0')}</span>
                  <h4 className="jba-display font-medium text-[#f8f6f0] text-lg sm:text-xl leading-[1.5]">{o.t}</h4>
                </div>
                <p className="font-['Noto_Serif_JP',serif] text-sm text-[#ded8cc] leading-[1.95]">{o.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 新バリエーション②（大数値+細罫スペック表+含有グリッド。由来: home IV Fee強調 + home VII 細罫テーブル） */}
      <NavyBand no="V" ja="実施形式と料金" en="Details & Fee" />
      <section className="py-12 sm:py-16 bg-white">
        <div className="max-w-[1680px] mx-auto px-4 sm:px-6 lg:px-10">
          <div className="grid lg:grid-cols-[360px_1fr] gap-10 lg:gap-14 items-start">
            <div className="bg-[#1a2942] border border-[#a38e65]/45 p-8 text-center">
              <div className="font-['Noto_Serif_JP',serif] text-sm text-[#d7b779] tracking-[0.1em] mb-3">受講料（税込）</div>
              <div className="font-['Playfair_Display',serif] text-[#f8f6f0] text-5xl sm:text-6xl leading-none">400,000<span className="text-2xl ml-1">円</span></div>
              <div className="w-11 h-px bg-[#a38e65] mx-auto mt-6 mb-5"></div>
              <p className="font-['Noto_Serif_JP',serif] text-sm text-[#ded8cc] leading-[1.9]">1日3時間・10-20名でのご提供です。</p>
            </div>
            <div>
              <dl className="border-t-2 border-[#1a2942]">
                {SPECS.map((s) => (
                  <div key={s.k} className="flex flex-col sm:flex-row gap-1 sm:gap-6 border-b border-[#e2ddd2] py-4">
                    <dt className="sm:w-[160px] sm:shrink-0 font-['Noto_Serif_JP',serif] text-sm text-[#8f7a56] tracking-[0.06em] pt-0.5">{s.k}</dt>
                    <dd className="flex-1 font-['Noto_Serif_JP',serif] text-[15px] sm:text-base text-[#1a2942] leading-[1.85]">{s.v}</dd>
                  </div>
                ))}
              </dl>
              <h4 className="jba-display font-medium text-[#1a2942] text-lg mt-10 mb-5">料金に含まれるもの</h4>
              <ul className="grid sm:grid-cols-2 gap-x-8">
                {INCLUDES.map((inc) => (
                  <li key={inc} className="flex items-start gap-3 border-b border-[#e2ddd2] py-3 font-['Noto_Serif_JP',serif] text-sm text-[#4a4638]"><i className="ri-check-line text-[#a38e65]"></i><span>{inc}</span></li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      <NavyBand no="VI" ja="お申込みの流れ" en="How to Apply" />
      <section className="py-12 sm:py-16 bg-white">
        <div className="max-w-[1680px] mx-auto px-4 sm:px-6 lg:px-10">
          <ol className="relative border-l border-[#e2ddd2] ml-5 max-w-4xl">
            {FLOW.map((s, i) => (
              <li key={s.t} className="relative pl-8 sm:pl-10 pb-9 last:pb-0">
                <span className="absolute -left-[19px] top-0 w-9 h-9 rounded-full bg-[#1a2942] text-[#d7b779] flex items-center justify-center font-['Playfair_Display',serif] text-sm border border-[#a38e65]/50">{i + 1}</span>
                <h4 className="jba-display font-medium text-[#1a2942] text-lg mb-1.5">{s.t}</h4>
                <p className="font-['Noto_Serif_JP',serif] text-sm text-[#4a4638] leading-[1.9]">{s.d}</p>
              </li>
            ))}
          </ol>
          <div className="bg-[#1a2942] border border-[#a38e65]/45 p-8 sm:p-10 mt-12 text-center">
            <h4 className="jba-display font-medium text-xl sm:text-2xl text-[#f8f6f0] mb-3">お申込み・お問い合わせ</h4>
            <p className="font-['Noto_Serif_JP',serif] text-base text-[#e7e2d8] mb-7 max-w-2xl mx-auto leading-[1.9]">異文化理解研修についてのご質問やお申込みは、お気軽にお問い合わせください。</p>
            <a href="/contact" className={BTN08_ON_NAVY}>異文化理解研修に申し込む</a>
          </div>
        </div>
      </section>

      <NavyBand no="VII" ja="よくある質問" en="FAQ" />
      <section className="py-12 sm:py-16 bg-white">
        <div className="max-w-[1680px] mx-auto px-4 sm:px-6 lg:px-10">
          <div className="border-t-2 border-[#1a2942] max-w-4xl">
            {FAQS.map((f) => (
              <div key={f.q} className="border-b border-[#e2ddd2] py-6">
                <h3 className="font-['Noto_Serif_JP',serif] font-medium text-[#1a2942] text-base sm:text-lg leading-[1.6] mb-2.5 flex gap-3"><span className="font-['Playfair_Display',serif] text-[#a38e65]">Q.</span><span>{f.q}</span></h3>
                <p className="font-['Noto_Serif_JP',serif] text-sm text-[#4a4638] leading-[1.95] flex gap-3"><span className="font-['Playfair_Display',serif] text-[#8f7a56]">A.</span><span>{f.a}</span></p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <NavyBand no="VIII" ja="関連研修プログラム" en="Related Programs" />
      <section className="py-12 sm:py-16 bg-white">
        <div className="max-w-[1680px] mx-auto px-4 sm:px-6 lg:px-10">
          <div className="grid md:grid-cols-3 gap-8">
            {RELATED.map((r) => (
              <a key={r.href} href={r.href} className="group bg-white border border-[#e2ddd2] p-8 flex flex-col hover:border-[#a38e65]/70 transition-colors duration-300">
                <h4 className="jba-display font-medium text-[#1a2942] text-lg sm:text-xl leading-[1.5] mb-3 group-hover:text-[#8f7a56] transition-colors">{r.label}</h4>
                <p className="font-['Noto_Serif_JP',serif] text-sm text-[#4a4638] leading-[1.9] mb-6">{r.desc}</p>
                <span className={`${TEXT_ARROW} mt-auto self-start`}>詳細を見る <span className="text-[#a38e65]">→</span></span>
              </a>
            ))}
          </div>
          <div className="text-center mt-12">
            <a href="/seminars" className={BTN08_ON_LIGHT}>セミナー・イベント一覧へ戻る</a>
          </div>
        </div>
      </section>
    </Layout>
  );
}
